import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import { useValidateProjectData } from '@material-bank/projects-utils';
import {
  shouldTriggerProjectSelectionDialog,
  Project,
  CustomEventName,
} from '@mb/lib';
import { isEuEnv, getUserLang } from '@mb/lib';
import { ProductCard } from 'components/Cards/ProductCard';
import { ProductCardActionType } from 'components/Cards/ProductCard/ProductCard.types';
import isEqual from 'lodash/isEqual';
import { useDispatch } from 'react-redux';

import { FinishGroupItem } from '../../../../pages/ProductSearch/components/Cards/ResultCard/types';
import { definedTranslation } from '../../../../pages/ProductSearch/i18n';
import {
  triggerBoardsModal,
  getIsPaintProductType,
  triggerAssociatedProductsModal,
} from '../../../../pages/ProductSearch/utils/helpers';
import { sendAnalytics } from '../../api/events';
import {
  mapProductToAnalyticsEcommerceItem,
  buildAnalyticsParamsToPDP,
} from '../../helpers/analytics';
import { useAddToBag } from '../../hooks/useAddToBag';
import { setMaterialsInQueue } from '../../redux/sliceCreators/commonSlice';

const userLang = getUserLang();
const urlPrefix = isEuEnv ? `/${userLang}` : '';

interface ssColor {
  color_name: string;
  entity_id: string | number;
  exact_color: string;
  image_url: string;
  manufacturer_sku: string;
  sku: string;
}

// TODO: describe types
export interface ResultCardProps extends Record<string, any> {
  currentProject: ProjectModel | null;
}

const ResultCard = React.memo<ResultCardProps>((props) => {
  const {
    material: product,
    imageSize = 252,
    selectedProject,
    currentProject,
    onProductClick,
    onCreateProjectClick,
    onProjectClick,
    projects,
    isMultiselect,
    onMaterialSelect,
    isSelected,
    cartItemsIds = [],
    isColorway = false,
    isMiniPDPFinish = false,
    isMobile,
    isHoverAllowed,
    isOutlined,
    relatedProductInfo = null,
    onContextMenuClick,
    onAddToCart,
    shouldTriggerRecModal = true,
    itemListId = 'visual_search',
    itemListName = 'Visual Search',
    analyticsEventLocation,
    index,
    onProjectSelect,
    onProjectCreate,
    intl,
  } = props;

  const {
    name,
    residentialCommercial,
    priceSign,
    entityId,
    manufacturer,
    manufacturerSku,
    ssFacetColorFilter,
    blurhash,
    isInRealTimeStock,
    isNew,
    isJustAdded,
    colorwayId,
    childrenCount,
    isCollection,
    associatedFinishProducts = [],
  } = product;

  const selectedSSFacetColor = useRef<ssColor | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const isInCart = cartItemsIds.includes(entityId);
  const itemId = Number(selectedSSFacetColor?.current?.entity_id) || entityId;
  const isPaint = getIsPaintProductType(product);
  const isProduct = product?.productFinishType?.includes('Product');
  const isFinishMaterial = product?.productFinishType?.includes('Finish');

  const { isLoading: isAddToCartLoading, addToBag } = useAddToBag();
  const { isProjectDataComplete } = useValidateProjectData();
  const dispatch = useDispatch();

  const cardLabels = {
    addToCartLabel: intl?.formatMessage(definedTranslation.card.addToCartLabel),
    addedToCartLabel: intl?.formatMessage(
      definedTranslation.card.addedToCartLabel,
    ),
    createProjectButtonLabel: intl?.formatMessage(
      definedTranslation.card.createProjectButtonLabel,
    ),
    inStockLabel: intl?.formatMessage(definedTranslation.card.inStockLabel),
    outOfStockLabel: intl?.formatMessage(
      definedTranslation.card.outOfStockLabel,
    ),
    projectPreLabel: intl?.formatMessage(
      definedTranslation.card.projectPreLabel,
    ),
    saveButtonLabel: intl?.formatMessage(
      definedTranslation.card.saveButtonLabel,
    ),
    savedButtonLabel: intl?.formatMessage(
      definedTranslation.card.savedButtonLabel,
    ),
    savingButtonLabel: intl?.formatMessage(
      definedTranslation.card.savingButtonLabel,
    ),
  };
  const internalLabels = {
    newLabel: intl?.formatMessage(definedTranslation.card.newLabel),
    justAddedLabel: intl?.formatMessage(definedTranslation.card.justAddedLabel),
    colorwaysLabel: intl?.formatMessage(definedTranslation.card.colorwaysLabel),
    orderNow: intl?.formatMessage(definedTranslation.card.orderNow),
    finishes: intl?.formatMessage(definedTranslation.card.finishes),
  };

  const sampleFinishesLabel = intl?.formatMessage(
    definedTranslation.card.sampleFinishesLabel,
  );

  useEffect(() => {
    const imageUrl = product.thumbnailImageUrl;

    // image from API comes with get param ?width=X, we need to replace that
    // to adjust size to column size
    const defaultSizeImage = imageUrl?.split('?')[0];
    const finalImageSize = Math.max(600, imageSize);
    setImageUrl(
      imageUrl ? `${defaultSizeImage}?width=${finalImageSize}` : null,
    );
  }, [product, imageSize, ssFacetColorFilter]);

  let materialUrl: string;
  const pdpTrackParams: string = buildAnalyticsParamsToPDP({
    product,
    itemListId,
    itemListName,
    index: index !== undefined ? index + 1 : undefined,
  });

  if (colorwayId != null) {
    materialUrl = `${product.url}?activeChild=${colorwayId}&${pdpTrackParams}`;
  } else if (selectedSSFacetColor?.current != null) {
    materialUrl = `${product.url}?activeChild=${selectedSSFacetColor?.current?.entity_id}&${pdpTrackParams}`;
  } else if (isFinishMaterial || !isCollection) {
    materialUrl = `${product.url}?activeChild=${entityId}&${pdpTrackParams}`;
  } else {
    materialUrl = `${product.url}?${pdpTrackParams}`;
  }

  materialUrl = `${urlPrefix}${materialUrl}`;

  const handleFavoritesClick = async () => {
    const currentItemId = itemId;
    const addToWishlistEventItem = mapProductToAnalyticsEcommerceItem({
      product,
      itemListId,
      itemListName,
      index: index !== undefined ? index + 1 : undefined,
      project: currentProject,
    });

    const currencies = {
      US: 'USD',
      EU: 'EUR',
      JP: 'JPY',
    };

    triggerBoardsModal({
      itemsData: [
        {
          id: currentItemId,
          type: isFinishMaterial ? 'finish' : 'material',
        },
      ],
      onSuccessAnalyticsEvents: [
        { ecommerce: null },
        {
          event: 'add_to_wishlist',
          list_id: itemListId,
          list_name: itemListName,
          method: 'Single',
          type: 'Materials',
          name: null,
          action: null,
          experiment_name: null,
          experiment_version: null,
          event_location: analyticsEventLocation ?? '',
          ecommerce: {
            currency: currencies[ENVIRONMENT_REGION],
            value: 0,
            items: [addToWishlistEventItem],
          },
        },
      ],
    });
    const stringIds = [String(currentItemId)];
    const value = {};
    await sendAnalytics({ id: stringIds, type: 'favorite', ...value });
  };

  const handleItemSelect = () => {
    onMaterialSelect?.({
      ...product,
      entityId: selectedSSFacetColor?.current?.entity_id || product.entityId,
    });
  };

  const handleAddToBoxClick = useCallback(() => {
    if (
      associatedFinishProducts?.length > 0 &&
      isFinishMaterial &&
      !isMiniPDPFinish
    ) {
      // If Finishes - trigger modal to select associated Products
      const productsIds = associatedFinishProducts.join('|');

      if (shouldTriggerProjectSelectionDialog()) {
        window.dispatchEvent(
          new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
            detail: {
              currentProjectId: selectedProject?.id,
              analyticsEventLocation,
              onCurrentProjectSave: (project: any) => {
                onProjectSelect?.(project?.externalId || project?.project_id);
                triggerAssociatedProductsModal(productsIds, {
                  id: entityId,
                  name,
                  manufacturerName: manufacturer,
                  productImageUrl: imageUrl,
                });
              },
              onProjectCreate: (project: Project) => {
                onProjectCreate?.(project);
              },
            },
          }),
        );
        return;
      }

      triggerAssociatedProductsModal(productsIds, {
        id: entityId,
        name,
        manufacturerName: manufacturer,
        productImageUrl: imageUrl,
      });
      return;
    }

    const currentProduct = {
      ...product,
      entityId: selectedSSFacetColor?.current?.entity_id || product.entityId,
    };
    // Add Related Product Info, if miniPDP finish
    const relatedProductData = isMiniPDPFinish ? relatedProductInfo : null;

    if (projects?.length > 0 && Boolean(selectedProject.id)) {
      const gaEventLocation = 'Visual Search';

      if (shouldTriggerProjectSelectionDialog()) {
        window.dispatchEvent(
          new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
            detail: {
              currentProjectId: selectedProject?.id,
              analyticsEventLocation,
              onCurrentProjectSave: (project: any) => {
                onProjectSelect?.(project?.externalId || project?.project_id);
                onAddToCart?.();

                // No validation needed here - the project has been validated in the selection dialog
                addToBag(
                  [currentProduct],
                  project.project_id,
                  project,
                  relatedProductData,
                  gaEventLocation,
                  isColorway,
                  shouldTriggerRecModal,
                );
              },
              onProjectCreate: (project: Project) => {
                onProjectCreate?.(project);
              },
            },
          }),
        );
        return;
      }

      // Complete any missing project data
      if (!currentProject || !isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: selectedProject.id,
          successCallback: async () => {
            onAddToCart?.();

            addToBag(
              [currentProduct],
              selectedProject.id,
              undefined,
              relatedProductData,
              gaEventLocation,
              isColorway,
              shouldTriggerRecModal,
            );
          },
        });

        return;
      }

      onAddToCart?.();

      addToBag(
        [currentProduct],
        selectedProject.id,
        undefined,
        relatedProductData,
        gaEventLocation,
        isColorway,
        shouldTriggerRecModal,
      );
    } else {
      onCreateProjectClick?.();
      dispatch(setMaterialsInQueue([currentProduct]));
    }
  }, [
    associatedFinishProducts,
    isFinishMaterial,
    isMiniPDPFinish,
    product,
    relatedProductInfo,
    projects?.length,
    selectedProject.id,
    entityId,
    name,
    manufacturer,
    imageUrl,
    analyticsEventLocation,
    onProjectSelect,
    onProjectCreate,
    isProjectDataComplete,
    currentProject,
    onAddToCart,
    addToBag,
    isColorway,
    shouldTriggerRecModal,
    onCreateProjectClick,
    dispatch,
  ]);

  const handleProductClick = (id?: string) => {
    if (isMultiselect) {
      onMaterialSelect?.(product);
    } else {
      const newUrl =
        id != null && colorwayId == null
          ? `${urlPrefix}${product.url}?activeChild=${id}`
          : materialUrl;
      onProductClick?.({
        ...product,
        url: newUrl,
        manufacturerSku:
          selectedSSFacetColor?.current?.manufacturer_sku || manufacturerSku,
        thumbnailImageUrl: imageUrl,
        entityId: id || itemId,
        isColorApplied: Boolean(
          selectedSSFacetColor?.current?.manufacturer_sku,
        ),
        index,
      });
    }
  };

  const handleRightClick = () => {
    onContextMenuClick?.({ ...product, entityId: itemId });
  };

  const handleProjectClick = useCallback(
    (id: number) => {
      onProjectClick?.(id, { analyticsEventLocation });
    },
    [analyticsEventLocation, onProjectClick],
  );

  const onCreateProject = useCallback(() => {
    onCreateProjectClick?.({ analyticsEventLocation });
  }, [analyticsEventLocation, onCreateProjectClick]);

  const usageList =
    residentialCommercial?.length > 0 ? residentialCommercial : null;

  const priceLabel = isPaint ? null : priceSign;
  const availableSubcategories: string[] =
    product.categories?.filter((item: string) => item.includes('>')) ?? [];
  const firstSubcategory = availableSubcategories?.[0]?.split('>')?.pop();
  const lastSubcategory = availableSubcategories?.[
    availableSubcategories.length - 1
  ]
    ?.split('>')
    ?.pop();
  const subcategoryToShow = product.categories?.includes(firstSubcategory)
    ? lastSubcategory
    : firstSubcategory;
  const usageType =
    usageList?.map((usage: string) => usage.split(' ')[0]) || [];
  const colorwaysNumber =
    ssFacetColorFilter?.length > 1
      ? `${ssFacetColorFilter?.length} ${internalLabels.colorwaysLabel}`
      : null;
  const features = [
    priceLabel,
    subcategoryToShow,
    ...usageType,
    colorwaysNumber,
  ].filter((item) => item != null);
  const count = Number(childrenCount) > 1 ? childrenCount : undefined;
  const isNewLabel = isNew
    ? [{ label: internalLabels.newLabel }]
    : isJustAdded
    ? [{ label: internalLabels.justAddedLabel }]
    : [];
  const blurhashString = blurhash?.length >= 6 ? blurhash : null;
  const finalCardUrl = isProduct
    ? materialUrl?.split('?activeChild')?.[0]
    : materialUrl;
  const cardLabel = isColorway ? product?.title : name;
  const actionType: ProductCardActionType = isProduct
    ? 'customAction'
    : 'addToCart';
  const showProjectsDropdown = !isMobile && !isProduct;
  const finishesCount =
    product.components?.reduce(
      (totalCount: number, finishGroup: { items: FinishGroupItem[] }) =>
        totalCount + (finishGroup.items?.length ?? 0),
      0,
    ) ?? 0;
  const finishesCountLabel = `${finishesCount} ${internalLabels.finishes}`;
  const taxonomyLabel = product.taxonomy?.[0]?.split('>')?.pop();
  const subtitle1 =
    isProduct && finishesCount > 0
      ? finishesCountLabel
      : subcategoryToShow || taxonomyLabel;

  return (
    <ProductCard
      title={manufacturer}
      label={cardLabel}
      subtitle1={subtitle1}
      subtitle2={cardLabel}
      entityId={entityId}
      imageUrl={imageUrl!}
      projects={projects}
      productUrl={finalCardUrl}
      features={features}
      topInfoItems={isNewLabel}
      isOvernight={isInRealTimeStock}
      isMobileDevice={isMobile}
      showAdvancedInfo
      showSelectOnHover={false}
      showSelect={isMultiselect}
      isHoverAllowed={isHoverAllowed}
      isOutlined={isOutlined}
      isSelected={isSelected}
      showHoverFeatures
      showBag
      childrenNumber={count}
      showProjectsDropdown={showProjectsDropdown}
      afterLabel={undefined}
      blurhash={blurhashString}
      showContainedInfo
      showExtraInfo={false}
      actionType={actionType}
      actionButtonText={sampleFinishesLabel}
      onClickAddToFavorites={isMobile ? undefined : handleFavoritesClick}
      selectedProject={selectedProject}
      onAddToBag={handleAddToBoxClick}
      isAddToCartLoading={isAddToCartLoading}
      isAddedToCart={isInCart}
      onProductClick={handleProductClick}
      onActionButtonClick={handleProductClick}
      onProjectClick={handleProjectClick}
      onSelected={handleItemSelect}
      onCreateProject={onCreateProject}
      onRightClick={handleRightClick}
      {...cardLabels}
    />
  );
}, isEqual);

ResultCard.displayName = 'ResultCard';

export { ResultCard };
